import React, { useEffect, useState } from "react";
import { Download, OctagonAlert, RefreshCw, Shield, Wifi, WifiOff, Power, AlertTriangle } from "lucide-react";
import { fetchTelemetry, setKillSwitch, exportTradesCsvUrl, exportPnlDailyCsvUrl } from "@/lib/api";
import { toast } from "sonner";

const fmtAgo = (ms) => {
    if (!ms) return "—";
    const dt = Date.now() - ms;
    if (dt < 0) return "now";
    const s = Math.floor(dt / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ago`;
};

const Pill = ({ children, color = "#A7B4C6", bg = "rgba(167,180,198,0.10)", border = "rgba(167,180,198,0.20)", testId }) => (
    <span
        data-testid={testId}
        className="inline-flex items-center gap-1.5 h-5 px-2 rounded-full text-[10px] uppercase tracking-[0.14em] font-medium font-mono"
        style={{ background: bg, color, border: `1px solid ${border}` }}
    >
        {children}
    </span>
);

export const OperationsPanel = ({ status, onRefresh }) => {
    const [tele, setTele] = useState(null);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try { const t = await fetchTelemetry(); if (alive) setTele(t); } catch (_) { /* ignore */ }
        };
        tick();
        const id = setInterval(tick, 3000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    const onKillToggle = async () => {
        const next = !status?.kill_switch;
        try {
            await setKillSwitch(next);
            toast[next ? "warning" : "success"](
                next ? "Kill-switch ENGAGED" : "Kill-switch released",
                { description: next ? "No new orders will be placed. Existing SELLs remain." : "Bot will resume placing new orders." },
            );
            onRefresh && onRefresh();
        } catch (e) {
            toast.error(`Kill-switch failed: ${e.message}`);
        }
    };

    const wsTel = tele?.ws || status?.ws_telemetry || {};
    const counters = tele?.counters || status?.counters || {};
    const isConnected = !!wsTel.is_connected;

    return (
        <div className="panel" data-testid="operations-panel">
            <div className="px-4 py-3 border-b border-[#1B2A3A] flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Shield size={14} color="#2EE6D6" />
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#A7B4C6]">
                        Operations & Security
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Pill testId="ops-bot-prefix" color="#2EE6D6"
                          bg="rgba(46,230,214,0.10)" border="rgba(46,230,214,0.25)">
                        Owned: {tele?.bot_prefix || status?.bot_prefix || "gd-"}*
                    </Pill>
                    {isConnected ? (
                        <Pill testId="ops-ws-state" color="#2BE38B"
                              bg="rgba(43,227,139,0.10)" border="rgba(43,227,139,0.25)">
                            <Wifi size={10} /> WS Online
                        </Pill>
                    ) : (
                        <Pill testId="ops-ws-state" color="#FFB020"
                              bg="rgba(255,176,32,0.10)" border="rgba(255,176,32,0.25)">
                            <WifiOff size={10} /> WS Offline
                        </Pill>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-12 gap-0">
                {/* Kill-switch */}
                <div className="col-span-12 lg:col-span-5 p-4 border-b lg:border-b-0 lg:border-r border-[#1B2A3A]">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6] mb-1">Kill-Switch</div>
                            <div className="text-xs text-[#6F7D92] max-w-sm">
                                Pauses placement of NEW orders. Existing SELL orders remain active to
                                close out open BUY fills. Use this when you want to wind down without
                                cancelling positions.
                            </div>
                        </div>
                        <button
                            onClick={onKillToggle}
                            className="btn-anim flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-medium"
                            style={status?.kill_switch ? {
                                background: "rgba(255,176,32,0.18)", color: "#FFB020",
                                border: "1px solid rgba(255,176,32,0.35)",
                            } : {
                                background: "transparent", color: "#FF5C6C",
                                border: "1px solid rgba(255,92,108,0.35)",
                            }}
                            data-testid="ops-kill-switch-button"
                        >
                            {status?.kill_switch ? <><OctagonAlert size={14} /> Engaged</> : <><Power size={14} /> Engage</>}
                        </button>
                    </div>
                </div>

                {/* Counters */}
                <div className="col-span-12 lg:col-span-4 p-4 border-b lg:border-b-0 lg:border-r border-[#1B2A3A]">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6] mb-2">Lifetime Counters</div>
                    <div className="grid grid-cols-2 gap-y-1.5 text-xs font-mono">
                        <div className="text-[#6F7D92]">BUYs placed</div>
                        <div className="text-right text-[#E6EDF6]" data-testid="ops-counter-buy-placed">{counters.buy_placed ?? 0}</div>
                        <div className="text-[#6F7D92]">SELLs placed</div>
                        <div className="text-right text-[#E6EDF6]" data-testid="ops-counter-sell-placed">{counters.sell_placed ?? 0}</div>
                        <div className="text-[#6F7D92]">BUY cancels</div>
                        <div className="text-right text-[#A7B4C6]">{counters.buy_cancel ?? 0}</div>
                        <div className="text-[#6F7D92]">BUY fills</div>
                        <div className="text-right text-[#2BE38B]">{counters.buy_fills ?? 0}</div>
                        <div className="text-[#6F7D92]">SELL fills</div>
                        <div className="text-right text-[#2BE38B]">{counters.sell_fills ?? 0}</div>
                        <div className="text-[#6F7D92]">Foreign ignored</div>
                        <div className="text-right text-[#FFB020]" data-testid="ops-counter-foreign-ignored">
                            {counters.foreign_orders_ignored ?? 0}
                        </div>
                    </div>
                </div>

                {/* WS telemetry */}
                <div className="col-span-12 lg:col-span-3 p-4">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6] mb-2">WebSocket Telemetry</div>
                    <div className="grid grid-cols-2 gap-y-1.5 text-xs font-mono">
                        <div className="text-[#6F7D92]">Connects</div>
                        <div className="text-right text-[#E6EDF6]">{wsTel.connect_count ?? 0}</div>
                        <div className="text-[#6F7D92]">Disconnects</div>
                        <div className="text-right text-[#A7B4C6]">{wsTel.disconnect_count ?? 0}</div>
                        <div className="text-[#6F7D92]">Last connect</div>
                        <div className="text-right text-[#A7B4C6]">{fmtAgo(wsTel.last_connect_ms)}</div>
                        <div className="text-[#6F7D92]">Backoff</div>
                        <div className="text-right text-[#A7B4C6]">
                            {wsTel.current_backoff_s ? `${wsTel.current_backoff_s.toFixed(1)}s` : "—"}
                        </div>
                        <div className="text-[#6F7D92]">Msgs</div>
                        <div className="text-right text-[#A7B4C6]">{wsTel.messages_received ?? 0}</div>
                    </div>
                    {wsTel.last_disconnect_reason && (
                        <div className="mt-2 flex items-start gap-1.5 text-[10px] text-[#FFB020]">
                            <AlertTriangle size={10} className="shrink-0 mt-0.5" />
                            <span className="truncate" title={wsTel.last_disconnect_reason}>
                                {wsTel.last_disconnect_reason}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Export bar */}
            <div className="px-4 py-3 border-t border-[#1B2A3A] flex flex-wrap items-center gap-2">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">Reports</div>
                <a
                    href={exportTradesCsvUrl()}
                    target="_blank" rel="noopener noreferrer"
                    className="btn-anim inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[11px] text-[#A7B4C6] hover:text-[#E6EDF6] border border-[#1B2A3A] hover:bg-[#121C28]"
                    data-testid="ops-export-trades-button"
                >
                    <Download size={12} /> Trades.csv
                </a>
                <a
                    href={exportPnlDailyCsvUrl(90)}
                    target="_blank" rel="noopener noreferrer"
                    className="btn-anim inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[11px] text-[#A7B4C6] hover:text-[#E6EDF6] border border-[#1B2A3A] hover:bg-[#121C28]"
                    data-testid="ops-export-pnl-button"
                >
                    <Download size={12} /> Daily-PnL.csv
                </a>
                <div className="flex-1" />
                <button
                    onClick={onRefresh}
                    className="btn-anim inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[11px] text-[#A7B4C6] hover:text-[#E6EDF6] border border-[#1B2A3A] hover:bg-[#121C28]"
                    data-testid="ops-refresh-button"
                >
                    <RefreshCw size={12} /> Refresh
                </button>
            </div>
        </div>
    );
};

export default OperationsPanel;
